/**
 * Invoice DTO exposed by rental-service `/api/invoices`.
 * Mirrors `net.nawam.rental.model.entity.Invoice` filtered by `IRead`.
 */

import type { Rental } from './rental.types';
import type { UserRef } from './user.types';

/** All possible invoice statuses (see `InvoiceStatusEnum`). */
export type InvoiceStatus =
  | 'PENDING'
  | 'PAID'
  | 'OVERDUE'
  | 'CANCELLED'
  | 'REFUNDED';

export interface Invoice {
  // ── BaseEntity ───────────────────────────────────────────────────────
  id: number;
  createdAt?: string | null;
  updatedAt?: string | null;

  // ── identity ──────────────────────────────────────────────────────────
  invoiceNumber: string;
  rental: Rental | null;
  /** Customer (renter) user id. */
  renterId?: number | null;
  /** Vehicle owner user id. */
  ownerId?: number | null;

  /** Read-model du locataire (alimenté par `@JoinColumn(referencedColumnName = "auth_user_id")`). */
  renterUser?: UserRef | null;
  /** Read-model du propriétaire du véhicule. */
  ownerUser?: UserRef | null;

  // ── dates ─────────────────────────────────────────────────────────────
  issueDate?: string | null;
  dueDate?: string | null;
  paidDate?: string | null;

  // ── financial (BigDecimal → string|number ; we use number for display) ──
  rentalAmount?: number | null;
  lateFee?: number | null;
  additionalCharges?: number | null;
  discount?: number | null;
  subtotal?: number | null;
  taxAmount?: number | null;
  totalAmount?: number | null;

  // ── status & docs ─────────────────────────────────────────────────────
  status: InvoiceStatus;
  pdfUrl?: string | null;
  notes?: string | null;
  paymentMethod?: string | null;
  paymentReference?: string | null;
}

import { defensiveStatusMeta } from '../helpers/status-meta';

/** UI helper : maps an invoice status to its label + Tailwind palette suffix.
 *  Defensive: unknown/null statuses fall back to `{label:'—', palette:'gray'}`. */
export const INVOICE_STATUS_META = defensiveStatusMeta<InvoiceStatus>({
  PENDING: { label: 'En attente', palette: 'warning' },
  PAID: { label: 'Payée', palette: 'brand' },
  OVERDUE: { label: 'En retard', palette: 'error' },
  CANCELLED: { label: 'Annulée', palette: 'gray' },
  REFUNDED: { label: 'Remboursée', palette: 'blue-light' },
});

/** Payload for `POST /api/invoices/{id}/mark-paid`. */
export interface MarkInvoicePaidRequest {
  paymentMethod: string;
  paymentReference?: string;
}
